import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useCallback, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, AlertCircle, Check, Loader2 } from 'lucide-react';
const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf'
];
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.pdf';
function generateId() {
    return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function isImageType(contentType) {
    return contentType.startsWith('image/');
}
export const AttachmentUploader = ({ pendingAttachments, onAttachmentsChange, disabled = false, maxFiles = 5, maxSizeBytes = 10 * 1024 * 1024 }) => {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const processFiles = useCallback((files) => {
        if (files.length === 0)
            return;
        const newAttachments = [];
        const currentCount = pendingAttachments.length;
        for (const file of files) {
            if (currentCount + newAttachments.length >= maxFiles) {
                break;
            }
            let error;
            if (!ALLOWED_TYPES.includes(file.type)) {
                error = 'File type not allowed';
            }
            else if (file.size > maxSizeBytes) {
                error = `File exceeds ${formatFileSize(maxSizeBytes)} limit`;
            }
            const attachment = {
                id: generateId(),
                file,
                status: error ? 'error' : 'pending',
                progress: 0,
                error
            };
            if (!error && isImageType(file.type)) {
                attachment.previewUrl = URL.createObjectURL(file);
            }
            newAttachments.push(attachment);
        }
        onAttachmentsChange([...pendingAttachments, ...newAttachments]);
    }, [pendingAttachments, onAttachmentsChange, maxFiles, maxSizeBytes]);
    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        processFiles(files);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [processFiles]);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragOver(true);
        }
    }, [disabled]);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (disabled)
            return;
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    }, [disabled, processFiles]);
    const handleRemove = useCallback((id) => {
        const attachment = pendingAttachments.find(a => a.id === id);
        if (attachment === null || attachment === void 0 ? void 0 : attachment.previewUrl) {
            URL.revokeObjectURL(attachment.previewUrl);
        }
        onAttachmentsChange(pendingAttachments.filter(a => a.id !== id));
    }, [pendingAttachments, onAttachmentsChange]);
    const handleButtonClick = () => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const canAddMore = pendingAttachments.length < maxFiles;
    const validAttachments = pendingAttachments.filter(a => !a.error);
    return (_jsxs("div", { className: `space-y-2 relative rounded transition-colors ${isDragOver ? 'ring-2 ring-dashed ring-green-500 bg-green-500/10' : ''}`, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [isDragOver && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center z-10 bg-black/50 rounded pointer-events-none", children: _jsx("span", { className: "text-sm font-medium", style: { color: 'var(--fern, #4ade80)' }, children: "Drop files here" }) })), _jsx("input", { ref: fileInputRef, type: "file", accept: ALLOWED_EXTENSIONS, multiple: true, onChange: handleFileSelect, className: "hidden", disabled: disabled }), pendingAttachments.length > 0 && (_jsx("div", { className: "flex flex-wrap gap-2", children: pendingAttachments.map((attachment) => (_jsxs("div", { className: "relative group rounded border overflow-hidden", style: {
                        borderColor: attachment.error ? '#ef4444' : 'var(--pewter)',
                        backgroundColor: 'rgba(0,0,0,0.3)'
                    }, children: [attachment.previewUrl ? (_jsx("img", { src: attachment.previewUrl, alt: attachment.file.name, className: "w-16 h-16 object-cover" })) : (_jsxs("div", { className: "w-16 h-16 flex flex-col items-center justify-center p-1", style: { color: 'var(--dew)' }, children: [attachment.error ? (_jsx(AlertCircle, { size: 20, className: "text-red-500" })) : isImageType(attachment.file.type) ? (_jsx(ImageIcon, { size: 20 })) : (_jsx(FileText, { size: 20 })), _jsx("span", { className: "text-[10px] truncate w-full text-center mt-1", children: attachment.file.name.slice(0, 8) })] })), _jsx("button", { onClick: () => handleRemove(attachment.id), disabled: disabled, className: "absolute top-0 right-0 p-0.5 bg-black/70 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity", style: { color: 'var(--sand)' }, children: _jsx(X, { size: 12 }) }), attachment.error && (_jsx("div", { className: "absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[8px] bg-red-900/90 text-red-200 truncate", title: attachment.error, children: attachment.error })), attachment.status === 'uploading' && (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute inset-0 bg-black/50 flex items-center justify-center", children: _jsx(Loader2, { size: 16, className: "animate-spin", style: { color: 'var(--fern)' } }) }), _jsx("div", { className: "absolute bottom-0 left-0 right-0 h-1 bg-black/50", children: _jsx("div", { className: "h-full transition-all", style: {
                                            width: `${attachment.progress}%`,
                                            backgroundColor: 'var(--fern, #4ade80)'
                                        } }) })] })), attachment.status === 'complete' && (_jsx("div", { className: "absolute top-0.5 left-0.5 p-0.5 bg-green-600 rounded-full", children: _jsx(Check, { size: 8, className: "text-white" }) }))] }, attachment.id))) })), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("button", { onClick: handleButtonClick, disabled: disabled || !canAddMore, className: "flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-white/10 transition-colors", style: {
                            color: canAddMore && !disabled ? 'var(--dew)' : 'var(--pewter)',
                            cursor: canAddMore && !disabled ? 'pointer' : 'not-allowed'
                        }, title: canAddMore ? 'Attach file (images or PDF)' : `Maximum ${maxFiles} files`, children: [_jsx(Paperclip, { size: 14 }), "Attach"] }), pendingAttachments.length > 0 && (_jsxs("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: [validAttachments.length, "/", maxFiles, " files"] }))] })] }));
};
